import React, { useState } from "react";
import { Button } from "./ui/button";
import { toast } from "./ui/sonner";
import { Loader2, Mic, Square, Sparkles } from "lucide-react";
import { useRecorder } from "../lib/useRecorder";
import { uploadChunked } from "../lib/upload";

/* 工作描述编辑：文字 + 语音录入（Whisper 转写 → AI 整理为工程要求条目 → 追加到描述，PM 核对后保存） */
export const RfqScopeEditor = ({ api, rfq, k, zh, value, onChange, onSave, onCancel, busy }) => {
  const [voice, setVoice] = useState(null);
  const [last, setLast] = useState(null);
  const process = async (blob) => {
    if (!blob.size) return;
    setVoice("upload");
    try {
      const ext = blob.type.includes("mp4") ? "mp4" : "webm";
      const d = await uploadChunked(api, blob, { name: `rfq_scope_voice.${ext}`, type: blob.type.split(";")[0] || "audio/webm" });
      setVoice("transcribe");
      const { data } = await api.post(`/rfq/${rfq.id}/scope/voice`, { fileId: d.fileId });
      setLast(data);
      onChange(((value || "").trimEnd() ? `${value.trimEnd()}\n` : "") + data.polished);
      toast.success(zh ? (data.ai ? "已转写并整理为工程要求，请核对后保存" : "已转写（AI 整理不可用，已插入原文），请核对后保存") : "Transcribed — please review and save");
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "语音处理失败" : "Voice failed")); }
    setVoice(null);
  };
  const { rec, sec, start, stop } = useRecorder({ maxSec: 180, onBlob: process });
  const begin = async () => {
    const err = await start();
    if (err === "unsupported") toast.error(zh ? "当前浏览器不支持录音，请用 Chrome / Safari 最新版" : "Recording not supported");
    else if (err) toast.error(zh ? "无法访问麦克风，请检查权限" : "Microphone blocked");
  };
  return (
    <div className="space-y-1" data-testid={`rfq-scope-editor-${k}`}>
      <textarea data-testid={`rfq-scope-input-${k}`} value={value} onChange={(e) => onChange(e.target.value)} rows={6} placeholder={zh ? "工作范围、工程要求、材料规范、注意事项…（可点击麦克风口述）" : "Scope, requirements, specs…"} className="w-full rounded border border-slate-300 p-2 text-[11px]" />
      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" data-testid={`rfq-scope-save-${k}`} disabled={busy || !!voice || rec} onClick={onSave} className="h-7 text-[11px]">{zh ? "保存" : "Save"}</Button>
        <button type="button" onClick={onCancel} className="text-slate-400 underline">{zh ? "取消" : "Cancel"}</button>
        {rec ? (
          <button type="button" data-testid={`rfq-scope-voice-stop-${k}`} onClick={stop} className="inline-flex items-center gap-1 rounded-full bg-red-600 text-white px-2.5 py-1 text-[11px] font-semibold animate-pulse"><Square className="h-3 w-3" />{zh ? `停止 · ${sec}s` : `Stop · ${sec}s`}</button>
        ) : (
          <button type="button" data-testid={`rfq-scope-voice-${k}`} disabled={!!voice} onClick={begin} className="inline-flex items-center gap-1 rounded-full border border-teal-300 text-teal-800 px-2.5 py-1 text-[11px] font-semibold hover:bg-teal-50 disabled:opacity-60">
            {voice ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mic className="h-3 w-3" />}{voice === "upload" ? (zh ? "上传录音…" : "Uploading…") : voice === "transcribe" ? (zh ? "转写整理中…" : "Transcribing…") : (zh ? "语音录入工程要求" : "Voice input")}
          </button>
        )}
        <span className="text-[10px] text-slate-400">{zh ? "口述后自动转写并整理为条目，追加到描述末尾" : "Speak → transcribed → appended as bullet items"}</span>
      </div>
      {last && (
        <details className="text-[10px] text-slate-500" data-testid={`rfq-scope-voice-transcript-${k}`}>
          <summary className="cursor-pointer inline-flex items-center gap-1"><Sparkles className="h-3 w-3 text-teal-600" />{zh ? "查看原始转写" : "Raw transcript"}</summary>
          <p className="mt-1 whitespace-pre-wrap bg-white/70 border border-slate-100 rounded p-1.5">{last.transcript}</p>
        </details>
      )}
    </div>
  );
};
